import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Col,
  Row,
  Form,
  Input,
  Button,
  Typography,
  Image,
  message,
  Alert,
} from "antd";
import { TeamOutlined, UserSwitchOutlined, ArrowRightOutlined, CheckOutlined, GiftOutlined } from "@ant-design/icons";
import { SignupWrapper } from "./Signup.style";
import Logo from "./../../assets/logo.svg";
import lgSlider from "./../../assets/lg-slider-1.png";
import { API_CONST } from "../../const";

const { Paragraph, Link } = Typography;

const Signup = () => {
  const [form] = Form.useForm();
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const benchShareToken = new URLSearchParams(location.search).get("benchShareToken") || "";
  const referralCode = new URLSearchParams(location.search).get("ref") || "";

  const onFinish = async (values) => {
    if (!selectedRole) {
      message.error("Please select whether you want to hire talent or be a talent partner");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_CONST.SIGNUP, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          contact: values.contact,
          company: values.company,
          website: values.website,
          designation: values.designation,
          password: values.password,
          role: selectedRole,
          ...(benchShareToken ? { benchShareToken } : {}),
          ...(referralCode ? { referralCode } : {}),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        message.success("Account created successfully! Your account is pending admin approval. You can log in to view information while awaiting approval.");
        navigate("/");
      } else {
        if (data.code === "EMAIL_EXISTS") {
          message.warning("This email is already registered. Redirecting to login...");
          setTimeout(() => navigate("/"), 2000);
        } else {
          message.error(data.message || "Signup failed. Please try again.");
        }
      }
    } catch (error) {
      message.error("Network error, please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <SignupWrapper>
      <Row className="signup-row">
        <Col span={12} md={12} sm={0} xs={0} className="left-panel">
          <div className="logo-section">
            <Image width={160} preview={false} src={Logo} />
          </div>
          
          <div className="hero-section">
            <h1 className="headline">
              Scale Your Tech Team <span className="highlight">FASTER</span>
            </h1>
            <Paragraph className="tagline">
              Top 1% vetted Tech professionals - ready to augment your team and build your NEXT BIG THING!
            </Paragraph>
            
            <div className="feature-badges">
              <div className="badge">
                <span className="check-icon"><CheckOutlined /></span>
                AI Matched Talents
              </div>
              <div className="badge">
                <span className="check-icon"><CheckOutlined /></span>
                BGV & Skill Verified
              </div>
              <div className="badge">
                <span className="check-icon"><CheckOutlined /></span>
                Enterprise Ready
              </div>
            </div>
          </div>

          <div className="product-image-section">
            <Image preview={false} src={lgSlider} className="product-image" />
          </div>

          <div className="trusted-section">
            <div className="trusted-label">TRUSTED BY</div>
            <div className="trusted-text">Top Enterprises, Leading Startups and other Venture Capital Firms</div>
            <div className="testimonial">
              <div className="quote">"HireNXT helped us close senior roles in 5 days."</div>
              <div className="author">- CTO, FinTech Startup</div>
            </div>
          </div>
        </Col>

        <Col span={12} md={12} sm={24} xs={24} className="right-panel">
          <div className="tech-pattern" />
          <div className="glass-sphere-1" />
          <div className="glass-sphere-2" />
          <div className="glass-sphere-3" />
          <div className="signup-form-container">
            {referralCode && (
              <Alert
                type="success"
                icon={<GiftOutlined />}
                showIcon
                style={{ marginBottom: 16, borderRadius: 8 }}
                message={
                  <span>
                    <strong>You were referred!</strong> Sign up as a Customer and you'll be automatically
                    linked with your referring partner.
                  </span>
                }
              />
            )}
            <h1 className="form-title">Get started with HireNXT</h1>
            <Paragraph className="form-subtitle">
              Join thousands of tech leaders hiring top-tier talent.
            </Paragraph>

            <Form
              form={form}
              name="signup"
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <div className="section-label">Select your account type</div>
              <div className="role-selection">
                <div
                  className={`role-card ${selectedRole === "Customer" ? "selected" : ""}`}
                  onClick={() => setSelectedRole("Customer")}
                >
                  <div className="role-icon">
                    <TeamOutlined />
                  </div>
                  <div className="role-content">
                    <div className="role-title">Hire Talent</div>
                    <div className="role-description">Find top 1% tech talent</div>
                  </div>
                  <div className="selection-indicator" />
                </div>

                <div
                  className={`role-card ${selectedRole === "Partner" ? "selected" : ""}`}
                  onClick={() => setSelectedRole("Partner")}
                >
                  <div className="role-icon">
                    <UserSwitchOutlined />
                  </div>
                  <div className="role-content">
                    <div className="role-title">Talent Partner</div>
                    <div className="role-description">Join our expert network</div>
                  </div>
                  <div className="selection-indicator" />
                </div>
              </div>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input placeholder="Your name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: "Required" },
                      { type: "email", message: "Invalid email" },
                    ]}
                  >
                    <Input placeholder="Work email" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    label="Contact"
                    name="contact"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input placeholder="Phone number" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Company"
                    name="company"
                  >
                    <Input placeholder="Company name" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[
                      { required: true, message: "Required" },
                      { min: 6, message: "Min 6 chars" },
                    ]}
                  >
                    <Input.Password placeholder="Password" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Confirm"
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                      { required: true, message: "Required" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Mismatch"));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="Confirm" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginBottom: 12 }}>
                <Button
                  className="submit-btn"
                  htmlType="submit"
                  loading={loading}
                  disabled={!selectedRole}
                >
                  Continue <ArrowRightOutlined />
                </Button>
              </Form.Item>

              <Paragraph className="login-text">
                Already a member?{" "}
                <Link className="login-link" onClick={() => navigate("/")}>
                  Sign in
                </Link>
              </Paragraph>
            </Form>
          </div>
        </Col>
      </Row>
    </SignupWrapper>
  );
};

export default Signup;
